import { action, ActionType } from 'typesafe-actions';
import type { Spacetime } from 'spacetime/types/types';

import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmEditStatusesEnum } from 'APIPackage/KnmModule/enums/KnmEditStatusesEnum';
import { KnmPublishStatusesEnum } from 'APIPackage/KnmModule/enums/KnmPublishStatusesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmDataViolation } from 'APIPackage/KnmModule/interfaces/IKnmDataViolation';
import { IKnmEditor } from 'APIPackage/KnmModule/interfaces/IKnmEditor';
import { IKnmParams } from 'APIPackage/KnmModule/interfaces/IKnmParams';
import { IKnmPlanFind } from 'APIPackage/KnmModule/interfaces/IKnmPlanFind';
import {
  accountChecklistTemplatesGet,
  accountRequirementTemplatesGet,
} from 'CorePackage/AccountModule/actions/accountActions';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IErrorFieldWithProps } from 'KnmPackage/MainModule/interfaces/IErrorFieldWithProps';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { IKnmExclusionReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmExclusionReasonSelectOption';
import { IKnmReasonChangeTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonChangeTypeSelectOption';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { ExportSimilaritiesListTypesEnum } from 'APIPackage/SimilarityModule/enums/ExportSimilaritiesListTypesEnum';
import { IRegistryExportOkvedFilterParams } from 'CorePackage/RegistryModule/interfaces/IRegistryExportOkvedFilterParams';
import { IApprovedSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IApprovedSelectOption';
import { IErknmInfoExcludeDatesParams } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesParams';
import { IErknmInfoExcludeDatesResponse } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesResponse';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { IKnmObject248 } from 'APIPackage/KnmModule/interfaces/IKnmObject248';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { IKnmRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa248';
import { IKnmErpForm } from 'KnmErpPackage/MainModule/interfaces/IKnmErpForm';
import { IFetchRequirementTemplatesParams } from 'APIPackage/AccountModule/services/requirementTemplatesApi';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IErknmPrescribeCancel } from 'APIPackage/KnmModule/interfaces/IErknmPrescribeCancel';

import { knmChecklistTemplatesGetBasicParams } from '../constants/knmChecklistTemplatesGetBasicParams';
import { knmRequirementTemplatesGetBasicParams } from '../constants/knmRequirementTemplatesGetBasicParams';
import { KnmActionsEnum } from '../enums/KnmActionsEnum';
import { KnmLoadingFlagsEnum } from '../enums/KnmLoadingFlagsEnum';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { IKnmForm } from '../interfaces/IKnmForm';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { IKnmStateLegalEntityData, IPrescribeCancelData } from '../types/IKnmState';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { IKnmHistoryChanges } from '../interfaces/IKnmHistoryChanges';
import { IKnmFormRenderProps } from '../components/KnmForm';
import { INKOForeignAgent } from '../../../APIPackage/CatalogsModule/interfaces/INKOForeignAgent';
import { IKnmAccessPrivilege } from '../../../APIPackage/KnmModule/interfaces/IKnmAccessPrivilege';
import {
  IAccessSettingsForm,
  IAccessSettingsFormRef,
} from '../../../KnmPmPackage/MainModule/components/AccessSettingsModal';
import { IJudgmentCourt } from '../../../APIPackage/CatalogsModule/interfaces/IJudgmentCourt';
import { IEpguNotification } from '../../../APIPackage/KnmModule/interfaces/IEpguNotification';
import { IErvkObjectsOfControl } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControl';
import { IKnmInspector248 } from '../../../APIPackage/KnmModule/interfaces/IKnmInspector248';
import { IKnmDecisionNpaItem } from '../../../APIPackage/KnmModule/interfaces/IKnmDecisionNpaItem';
import { IKnmPunishmentNature } from '../../../APIPackage/KnmModule/interfaces/IKnmPunishmentNature';
import { IValidatedUnsignedKnmByPlan } from '../../../APIPackage/KnmModule/interfaces/IValidatedUnsignedKnmByPlan';
import { IKnmOrderSelectOption } from '../interfaces/IKnmOrderSelectOption';
import { IKnmErknmFormChangePlanReason } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChangePlanReason';
import { IErknmStatusHintField } from '../interfaces/IErknmStatusHintField';
import { IErvkCorrectionRequiredData } from '../../../APIPackage/KnmModule/interfaces/IErvkCorrectionRequiredData';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IKnoOrganisationChild } from '../../../APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import { IKnmHistoryItem } from '../../../APIPackage/KnmModule/interfaces/IKnmHistoryItem';

export const knmCatalogsInit = () => action(KnmActionsEnum.KNM_CATALOGS_INIT);
export const knmCreate = (data: IKnmParams) => action(KnmActionsEnum.KNM_CREATE, { data });
export const knmReset = () => action(KnmActionsEnum.KNM_RESET);
export const knmSign = (
  certificate: string,
  id?: IKnm['id'],
  signer?: IKnmInspector248,
  actGuid?: string,
  prescriptionGuid?: string,
  isCancelSign?: boolean,
) => action(KnmActionsEnum.KNM_SIGN, { certificate, id, actGuid, signer, prescriptionGuid, isCancelSign });
export const knmSignWithSaveSigner = (
  certificate: string,
  id?: IKnm['id'],
  signer?: IKnmInspector248,
  actGuid?: string,
  prescriptionGuid?: string,
  isCancelSign?: boolean,
) =>
  action(KnmActionsEnum.KNM_SIGN_WITH_SAVE_SIGNER, {
    certificate,
    id,
    signer,
    actGuid,
    prescriptionGuid,
    isCancelSign,
  });
export const knmTestSign = (id?: IKnm['id']) => action(KnmActionsEnum.KNM_TEST_SIGN, { id });
export const knmTestSignWithSaveSigner = (id?: IKnm['id'], signer?: IKnmInspector248) =>
  action(KnmActionsEnum.KNM_TEST_SIGN_WITH_SAVE_SIGNER, { id, signer });
export const knmSetActionsDropdownVisible = (visible: boolean) =>
  action(KnmActionsEnum.KNM_SET_ACTIONS_DROPDOWN_VISIBLE, { visible });
export const knmSetUrlId = (urlId: string) => action(KnmActionsEnum.KNM_SET_URL_ID, { urlId });
export const knmSetRawData = (rawData: IKnm | undefined) => action(KnmActionsEnum.KNM_SET_RAW_DATA, { rawData });
export const knmPatchRawData = (partRawData: Partial<IKnm>) =>
  action(KnmActionsEnum.KNM_PATCH_RAW_DATA, { partRawData });
export const knmDownloadCurrentVersion = () => action(KnmActionsEnum.KNM_DOWNLOAD_CURRENT_VERSION);
export const knmRestoreToLatestVersion = () => action(KnmActionsEnum.KNM_RESTORE_LATEST_SIGNED_VERSION);
export const knmSetErknmKnoOrganizationFormValue = (
  knoOrganization: IKnmErknmForm['knoOrganizationErknm'] | undefined,
) => action(KnmActionsEnum.KNM_SET_ERKNM_KNO_ORGANIZATION_FORM_VALUE, { knoOrganization });
export const knmSetErknmSupervisionTypeFormValue = (supervisionType: IKnmErknmForm['kindControl'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_ERKNM_SUPERVISION_TYPE_FORM_VALUE, { supervisionType });
export const knmSetErknmKnmKindVersionIdFormValue = (knmKindVersionId: string | undefined) =>
  action(KnmActionsEnum.KNM_SET_ERKNM_KNM_KIND_VERSION_ID_FORM_VALUE, { knmKindVersionId });
export const knmSetErrorFields = (errorFields: Array<IErrorFieldWithProps>) =>
  action(KnmActionsEnum.KNM_SET_ERROR_FIELDS, { errorFields });
export const knmSetStatusHintText = (value: string | undefined) =>
  action(KnmActionsEnum.KNM_SET_STATUS_HINT_TEXT, { value });
export const knmSetStatusHintFields = (fields: IErknmStatusHintField[]) =>
  action(KnmActionsEnum.KNM_SET_STATUS_HINT_FIELDS, { fields });
export const knmSetFormRef = (formRef: IKnmFormRef) => action(KnmActionsEnum.KNM_SET_FORM_REF, { formRef });
export const knmSetKnoOrganizationFormValue = (knoOrganization: IKnmForm['knoOrganization'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_KNO_ORGANIZATION_FORM_VALUE, { knoOrganization });
export const knmSetKnoOrganizationErvkFormValue = (knoOrganizationErvk: IKnmForm['knoOrganizationErvk'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_KNO_ORGANIZATION_ERVK_FORM_VALUE, { knoOrganizationErvk });
export const knmSetFederalLawFormValue = (federalLaw: IKnmForm['federalLaw'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_FEDERAL_LAW_FORM_VALUE, { federalLaw });
export const knmSetLegalEntityData = (data: IKnmStateLegalEntityData | undefined) =>
  action(KnmActionsEnum.KNM_SET_LEGAL_ENTITY_DATA, { data });
export const knmSetLoadingFlags = (name: KnmLoadingFlagsEnum, value: boolean) =>
  action(KnmActionsEnum.KNM_SET_LOADING_FLAGS, { name, value });
export const knmSetPath = (path: string) => action(KnmActionsEnum.KNM_SET_PATH, { path });
export const knmSetPublishedStatus = (publishedStatus: KnmPublishStatusesEnum | undefined) =>
  action(KnmActionsEnum.KNM_SET_PUBLISHED_STATUS, { publishedStatus });
export const knmSetEditStatus = (status: KnmEditStatusesEnum | undefined) =>
  action(KnmActionsEnum.KNM_SET_EDIT_STATUS, { status });
export const knmSetEditorTime = (time?: Spacetime) => action(KnmActionsEnum.KNM_SET_EDITOR_TIME, { time });
export const knmSetPrevKnmEditors = (knmEditors: IKnmEditor[] | undefined) =>
  action(KnmActionsEnum.KNM_SET_PREV_KNM_EDITORS, { knmEditors });
export const knmSetStatus = (status: IKnmStatus | undefined) => action(KnmActionsEnum.KNM_SET_STATUS, { status });
export const knmSetStatusText = (statusText: string | undefined) =>
  action(KnmActionsEnum.KNM_SET_STATUS_TEXT, { statusText });
export const knmSetSubjectTypeFormValue = (subjectType: IKnmForm['subjectType'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_SUBJECT_TYPE_FORM_VALUE, { subjectType });
export const knmSetSupervisionTypeFormValue = (supervisionType: IKnmForm['supervisionType'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_SUPERVISION_TYPE_FORM_VALUE, { supervisionType });
export const knmSetSupervisionTypeErvkFormValue = (supervisionTypeErvk: IKnmForm['supervisionTypeErvk'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_SUPERVISION_TYPE_ERVK_FORM_VALUE, { supervisionTypeErvk });
export const knmSetTypeFormValue = (type: IKnmForm['type'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_TYPE_FORM_VALUE, { type });
export const knmSetTypeErvkFormValue = (type: IKnmForm['typeErvk'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_TYPE_ERVK_FORM_VALUE, { type });
export const knmSetSystemType = (systemType: KnmSystemTypesEnum | undefined) =>
  action(KnmActionsEnum.KNM_SET_SYSTEM_TYPE, { systemType });
export const knmSetSupervisionType = () => action(KnmActionsEnum.KNM_SET_PM_SUPERVISION_TYPE);
export const knmSetIsChangePlanReasonActive = (isChangePlanReasonActive: boolean) =>
  action(KnmActionsEnum.KNM_SET_IS_CHANGE_PLAN_REASON_ACTIVE, { isChangePlanReasonActive });
export const knmSetOnSaveButtonClick = (onSaveButtonClick: () => void) =>
  action(KnmActionsEnum.KNM_SET_ON_SAVE_BUTTON_CLICK, { onSaveButtonClick });
export const knmSetOnAdminSaveButtonClick = (onAdminSaveButtonClick: (changeReason: string) => void) =>
  action(KnmActionsEnum.KNM_SET_ON_ADMIN_SAVE_BUTTON_CLICK, { onAdminSaveButtonClick });
export const knmSetOpenChangeKnmModal = (openChangeKnmModal: () => void) =>
  action(KnmActionsEnum.KNM_SET_OPEN_CHANGE_KNM_MODAL, { openChangeKnmModal });
export const knmSetOpenRemoveFromPlanModal = (openRemoveFromPlanModal: () => void) =>
  action(KnmActionsEnum.KNM_SET_OPEN_REMOVE_FROM_PLAN_MODAL, { openRemoveFromPlanModal });
export const knmSetPublishKnm = (publishKnm: () => void) => action(KnmActionsEnum.KNM_SET_PUBLISH_KNM, { publishKnm });
export const knmSetEndByErknm = (knmEndByErknm: () => void) =>
  action(KnmActionsEnum.KNM_SET_KNM_END_BY_ERKNM, { knmEndByErknm });
export const knmSetUnpublishKnm = (unpublishKnm: () => void) =>
  action(KnmActionsEnum.KNM_SET_UNPUBLISH_KNM, { unpublishKnm });
export const knmSetDownloadProsecutorDocs = (downloadProsecutorDocs: () => void) =>
  action(KnmActionsEnum.KNM_SET_DOWNLOAD_PROSECUTOR_DOCS, { downloadProsecutorDocs });
export const knmPlanGetNpa = (dictVersionId: string) => action(KnmActionsEnum.KNM_PLAN_GET_NPA, { dictVersionId });
export const knmPlanGetNpaSuccess = (data: IKnmDecisionNpaItem[]) =>
  action(KnmActionsEnum.KNM_PLAN_GET_NPA_SUCCESS, { data });
export const knmPlanGetPunishmentNature = () => action(KnmActionsEnum.KNM_PLAN_GET_PUNISHMENT_NATURE);
export const knmPlanGetPunishmentNatureSuccess = (data: IKnmPunishmentNature[]) =>
  action(KnmActionsEnum.KNM_PLAN_GET_PUNISHMENT_NATURE_SUCCESS, { data });
export const knmPlanFindGet = (planId: number) => action(KnmActionsEnum.KNM_PLAN_FIND_GET, { planId });

export const validateUnsignedKnmByPlanIdPost = (
  planId: number,
  fioSigner: string,
  fioSignerCN: string,
  organizationName: string,
) =>
  action(KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM, {
    planId,
    fioSigner,
    fioSignerCN,
    organizationName,
  });
export const validateUnsignedKnmByPlanIdClear = () => action(KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_CLEAR);
export const validateUnsignedKnmByPlanIdRequest = () => action(KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_REQUEST);
export const validateUnsignedKnmByPlanIdSuccess = (data: IValidatedUnsignedKnmByPlan) =>
  action(KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_SUCCESS, { data });
export const validateUnsignedKnmByPlanIdFailure = () => action(KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM_FAILURE);

export const knmPlanFindGetRequest = () => action(KnmActionsEnum.KNM_PLAN_FIND_GET_REQUEST);
export const knmPlanFindGetSuccess = (data: IKnmPlanFind) => action(KnmActionsEnum.KNM_PLAN_FIND_GET_SUCCESS, { data });
export const knmPlanFindGetFailure = () => action(KnmActionsEnum.KNM_PLAN_FIND_GET_FAILURE);
export const knmPlanFindGetEnd = () => action(KnmActionsEnum.KNM_PLAN_FIND_GET_END);
export const knmViolationsGetRequest = () => action(KnmActionsEnum.KNM_VIOLATIONS_GET_REQUEST);
export const knmViolationsGetSuccess = (data: IKnmDataViolation[]) =>
  action(KnmActionsEnum.KNM_VIOLATIONS_GET_SUCCESS, { data });
export const knmViolationsGetFailure = () => action(KnmActionsEnum.KNM_VIOLATIONS_GET_FAILURE);
export const knmChecklistTemplatesGet = () =>
  accountChecklistTemplatesGet({
    ...knmChecklistTemplatesGetBasicParams,
    isCaching: true,
  });
export const knmRequirementTemplatesGet = () =>
  accountRequirementTemplatesGet({
    ...knmRequirementTemplatesGetBasicParams,
    isCaching: true,
  });
export const knmRequirementTemplatesByKnoOrganizationIdAndSupervisionTypeIdGet = (
  data: IFetchRequirementTemplatesParams,
) =>
  accountRequirementTemplatesGet({
    ...knmRequirementTemplatesGetBasicParams,
    ...data,
    isCaching: true,
  });
export const knmKnoOrganizationFunctionsGet = () => action(KnmActionsEnum.KNM_KNO_ORGANIZATION_FUNCTIONS_GET);
export const knmGet = (onSuccess?: (data: IKnm) => void) => action(KnmActionsEnum.KNM_GET, { onSuccess });
export const knmGetRequest = () => action(KnmActionsEnum.KNM_GET_REQUEST);
export const knmGetSuccess = (data: IKnm) => action(KnmActionsEnum.KNM_GET_SUCCESS, { data });
export const knmGetFailure = () => action(KnmActionsEnum.KNM_GET_FAILURE);
export const knmUpdate = (data: IKnmParams) => action(KnmActionsEnum.KNM_UPDATE, { data });
export const knmUpdateRequest = () => action(KnmActionsEnum.KNM_UPDATE_REQUEST);
export const knmAdminUpdate = (data: IKnmParams, changeReason: string) =>
  action(KnmActionsEnum.KNM_ADMIN_UPDATE, { data, changeReason });
export const knmUpdateSuccess = (data: IKnm) => action(KnmActionsEnum.KNM_UPDATE_SUCCESS, { data });
export const knmUpdateFailure = () => action(KnmActionsEnum.KNM_UPDATE_FAILURE);
export const knmDraftSave = (data: IKnmParams) => action(KnmActionsEnum.KNM_DRAFT_SAVE, { data });
export const knmDraftDelete = (id: number, callback?: () => void, shouldReload?: boolean) =>
  action(KnmActionsEnum.KNM_DRAFT_DELETE, { id, callback, shouldReload });
export const knmCopy = () => action(KnmActionsEnum.KNM_COPY);
export const knmCopyRequest = () => action(KnmActionsEnum.KNM_COPY_REQUEST);
export const knmCopySuccess = (data: IKnm) => action(KnmActionsEnum.KNM_COPY_SUCCESS, { data });
export const knmCopyFailure = () => action(KnmActionsEnum.KNM_COPY_FAILURE);
export const knmExcludeFromPlan = (data?: IKnmMoveToPlanAttachment | IKnmExclusionReasonSelectOption) =>
  action(KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN, { data });
export const knmExcludeFromPlanRequest = () => action(KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN_REQUEST);
export const knmExcludeFromPlanSuccess = () => action(KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN_SUCCESS);
export const knmExcludeFromPlanFailure = () => action(KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN_FAILURE);
export const knmPlanExclusionReasonsGet = (knmIds: number[]) =>
  action(KnmActionsEnum.KNM_PLAN_EXCLUSION_REASONS_GET, { knmIds });
export const knmPlanExclusionReasonsSet = (options: IKnmReasonChangeTypeSelectOption[]) =>
  action(KnmActionsEnum.KNM_PLAN_EXCLUSION_REASONS_SET, { options });
export const knmPlanExclusionReasonsReset = () => action(KnmActionsEnum.KNM_PLAN_EXCLUSION_REASONS_RESET);
export const knmInitEditorsUpdate = () => action(KnmActionsEnum.KNM_INIT_EDITORS_UPDATE);
export const knmAddVisibleModal = (value: KnmModalsEnum, params?: { [key: string]: unknown }) =>
  action(KnmActionsEnum.KNM_ADD_VISIBLE_MODALS, { value, params });
export const knmRemoveVisibleModal = (value: KnmModalsEnum) =>
  action(KnmActionsEnum.KNM_REMOVE_VISIBLE_MODALS, { value });
export const knmAttachWebDocument = (data: IKnmAttachment) => action(KnmActionsEnum.KNM_ATTACH_WEB_DOCUMENT, { data });
export const knmExportKnm = (format: ExportFormatsEnum) => action(KnmActionsEnum.KNM_EXPORT_KNM, { format });
export const knmChangeStatus = (commentText?: string) => action(KnmActionsEnum.KNM_CHANGE_STATUS, { commentText });
export const knmExportSimilaritiesList = (format: ExportFormatsEnum, type: ExportSimilaritiesListTypesEnum) =>
  action(KnmActionsEnum.KNM_EXPORT_SIMILARITIES_LIST, { format, type });
export const knmGetHistoryChanges = () => action(KnmActionsEnum.KNM_GET_HISTORY_CHANGES);
export const knmSetHistoryChanges = (data: IKnmHistoryChanges) =>
  action(KnmActionsEnum.KNM_SET_HISTORY_CHANGES, { data });
export const knmGetSignedHistoryChanges = (startDateTime: string, endDateTime: string) =>
  action(KnmActionsEnum.KNM_GET_SIGNED_HISTORY_CHANGES, { startDateTime, endDateTime });
export const knmSetSignedHistoryChanges = (data: IKnmHistoryChanges) =>
  action(KnmActionsEnum.KNM_SET_SIGNED_HISTORY_CHANGES, { data });
export const knmSetHistoryChangesIsLoading = (value: boolean) =>
  action(KnmActionsEnum.KNM_SET_HISTORY_CHANGES_IS_LOADING, { value });
export const knmSetHistoryChangesIsOn = (value: boolean) =>
  action(KnmActionsEnum.KNM_SET_HISTORY_CHANGES_IS_ON, { value });
export const knmTryOpenSignModal = (
  actGuid?: string,
  prescriptionGuid?: string,
  isNotNeedSubmitForm?: boolean,
  onClickButtonSignBeforeInnerAction?: () => Promise<void>,
  abortCheckCertificate?: () => void,
  onSuccessCheckCertificate?: (action?: ActionType<typeof knmSign> | ActionType<typeof knmSignWithSaveSigner>) => void,
  isSignAfterSuccessCheckCertificateAction?: boolean,
  isNeedSubjectFioCheck?: boolean,
  signerSubjectFioForCheck?: string,
) =>
  action(KnmActionsEnum.KNM_TRY_OPEN_SIGN_MODAL, {
    actGuid,
    prescriptionGuid,
    isNotNeedSubmitForm,
    onClickButtonSignBeforeInnerAction,
    abortCheckCertificate,
    onSuccessCheckCertificate,
    isSignAfterSuccessCheckCertificateAction,
    isNeedSubjectFioCheck,
    signerSubjectFioForCheck,
  });
export const knmTryGenerateDecision = (id: string, isDoc: boolean) =>
  action(KnmActionsEnum.KNM_TRY_GENERATE_DECISION, { id, isDoc });
export const knmAgreementTemplateDownload = (agreement: IErknmFormAgreement) =>
  action(KnmActionsEnum.KNM_AGREEMENT_TEMPLATE_DOWNLOAD, { agreement });
export const knmDownloadXml = () => action(KnmActionsEnum.KNM_DOWNLOAD_XML);
export const knmDeleteSign = () => action(KnmActionsEnum.KNM_DELETE_SIGN);

export const knmDownloadSignedZip = () => action(KnmActionsEnum.KNM_DOWNLOAD_SIGNED_ZIP);
export const knmRestore = () => action(KnmActionsEnum.KNM_RESTORE);
export const knmReload = (callback?: () => void) => action(KnmActionsEnum.KNM_RELOAD, { callback });
export const knmHistoryReload = () => action(KnmActionsEnum.KNM_HISTORY_RELOAD);
export const knmDelete = () => action(KnmActionsEnum.KNM_DELETE);
export const knmComplete336Success = (data: IKnm) => action(KnmActionsEnum.KNM_COMPLETE336_SUCCESS, { data });
export const knmSetSubjectData = (subjectData?: Partial<IRegistryExportOkvedFilterParams>) =>
  action(KnmActionsEnum.KNM_SET_SUBJECT_DATA, { subjectData });
export const knmSetSelectedPrescribeCancelData = (prescribeData: IPrescribeCancelData) =>
  action(KnmActionsEnum.KNM_SET_PRESCRIBE_DATA, { prescribeData });
export const knmSetPrescribeCancelSigned = (isSignComplete: boolean) =>
  action(KnmActionsEnum.KNM_SET_PRESCRIBE_SIGNED, { isSignComplete });
export const knmMakeChangeSubjectTypeAvailable = (isAvailable = true) =>
  action(KnmActionsEnum.KNM_SET_AVAILABILITY_OF_CHANGING_SUBJECT_TYPE, { isAvailable });
export const knmHandleUploadErknmAttachWebDocument = (
  document: File | null,
  approvedValue: IApprovedSelectOption | undefined,
  signs: File[],
) => action(KnmActionsEnum.KNM_HANDLE_UPLOAD_ERKNM_ATTACH_WEB_DOCUMENT, { document, approvedValue, signs });
export const knmCreatePlannedKnm = (
  knmParams: IKnmParams | null,
  setKnmParams: (params: IKnmParams | null) => void,
  setIsAddKnmToPlanVisible: (value: boolean) => void,
) => action(KnmActionsEnum.KNM_CREATE_PLANNED_KNM, { knmParams, setKnmParams, setIsAddKnmToPlanVisible });
export const knmSignUploadModalApply = () => action(KnmActionsEnum.KNM_SIGN_UPLOAD_MODAL_APPLY);
export const knmSignSubmitFail = (errors: IKnmFormRenderProps['errors']) =>
  action(KnmActionsEnum.KNM_SIGN_SUBMIT_FAIL, { errors });
export const knmInfoGetExcludeDates = (data: IErknmInfoExcludeDatesParams) =>
  action(KnmActionsEnum.KNM_INFO_GET_EXCLUDE_DATES, { data });
export const knmInfoSetExcludeDates = (data: IErknmInfoExcludeDatesResponse) =>
  action(KnmActionsEnum.KNM_INFO_SET_EXCLUDE_DATES, { data });
export const knmInfoGetWorkDaysCountBeforeStart = (startDate: string) =>
  action(KnmActionsEnum.KNM_INFO_GET_WORK_DAYS_COUNT_BEFORE_START, { startDate });
export const knmInfoSetWorkDaysCountBeforeStart = (count: number) =>
  action(KnmActionsEnum.KNM_INFO_SET_WORK_DAYS_COUNT_BEFORE_START, { count });
export const knmInfoGetPlannedActivities = (knoId: string, supervisionId: string) =>
  action(KnmActionsEnum.KNM_GET_PLANNED_ACTIVITIES, { knoId, supervisionId });
export const knmInfoSetPlannedActivities = (data: boolean) =>
  action(KnmActionsEnum.KNM_SET_PLANNED_ACTIVITIES, { data });
export const knmInfoGetNKOForeignAgentDataByOgrn = (ogrn: string) =>
  action(KnmActionsEnum.KNM_GET_NKO_FOREIGN_AGENT_DATA_BY_OGRN, { ogrn });
export const knmInfoSetNKOForeignAgentDataByOgrn = (data: INKOForeignAgent | undefined) =>
  action(KnmActionsEnum.KNM_SET_NKO_FOREIGN_AGENT_DATA_BY_OGRN, { data });
export const knmInfoGetIsOrgInSonkoRegistry = (ogrn: string | undefined, inn: string | undefined) =>
  action(KnmActionsEnum.KNM_GET_IS_ORG_IN_SONKO_REGISTRY, { ogrn, inn });
export const knmInfoSetIsOrgInSonkoRegistry = (ogrn: string | undefined, inn: string | undefined, isPresent: boolean) =>
  action(KnmActionsEnum.KNM_SET_IS_ORG_IN_SONKO_REGISTRY, { ogrn, inn, isPresent });
export const knmUpdateAccessPrivilege = (data: IKnmAccessPrivilege[]) =>
  action(KnmActionsEnum.KNM_UPDATE_ACCESS_PRIVILEGE, { data });
export const knmSetAccessSettingsFormRef = (formRef: IAccessSettingsFormRef) =>
  action(KnmActionsEnum.KNM_SET_ACCESS_SETTINGS_FORM_REF, { formRef });
export const knmSetInitialAccessSettingsFormValues = (data: IAccessSettingsForm) =>
  action(KnmActionsEnum.KNM_SET_INITIAL_ACCESS_SETTINGS_FORM_VALUES, { data });
export const knmSetAccessNoticeErpId = (data: string | undefined) =>
  action(KnmActionsEnum.KNM_SET_ACCESS_NOTICE_ERP_ID, { data });
export const knmSetAccessNoticeKnoOrganizationName = (data: string | undefined) =>
  action(KnmActionsEnum.KNM_SET_ACCESS_NOTICE_KNO_ORGANIZATION_NAME, { data });
export const setCreatedJudgmentCourt = (data: IJudgmentCourt) =>
  action(KnmActionsEnum.KNM_SET_CREATED_JUDGMENT_COURT, { data });
export const knmInfoGetEpguNotificationList = (
  organizationGuid: string,
  type: string,
  shouldIgnoreTotalCount?: boolean,
) => action(KnmActionsEnum.KNM_GET_EPGU_NOTIFICATION_LIST, { organizationGuid, type, shouldIgnoreTotalCount });
export const knmInfoAddEpguNotificationList = (organizationGuid: string, data: IEpguNotification[]) =>
  action(KnmActionsEnum.KNM_ADD_EPGU_NOTIFICATION_LIST, { organizationGuid, data });
export const knmInfoSetEpguNotificationTotalCount = (organizationGuid: string, count: number) =>
  action(KnmActionsEnum.KNM_SET_EPGU_NOTIFICATION_TOTAL_COUNT, { organizationGuid, count });
export const knmSetApproveAddNotificationFunction = (approveFunction: (v: boolean) => void) =>
  action(KnmActionsEnum.KNM_SET_APPROVE_ADD_NOTIFICATION_FUNCTION, { approveFunction });
export const knmInfoGetErvkObjectsOfControl = (
  cosIdFilter: string | undefined,
  textFilter: string | undefined,
  riskCategoryIds: string[] | undefined,
  dangerClassIds: string[] | undefined,
  isDecision?: boolean,
) =>
  action(KnmActionsEnum.KNM_GET_ERVK_OBJECTS_OF_CONTROL, {
    cosIdFilter,
    textFilter,
    riskCategoryIds,
    dangerClassIds,
    isDecision,
  });
export const knmInfoAddErvkObjectOfControl = (data: IErvkObjectsOfControl[]) =>
  action(KnmActionsEnum.KNM_ADD_ERVK_OBJECTS_OF_CONTROL, { data });
export const knmGetControlObjectsByNonErvkOrganization = (
  organizationSubjectCode: string,
  inn: string | null,
  ogrn: string | null,
  ogrnip: string | null,
  kpp: string | null,
  snils: string | null,
  rafpNza: string | null,
  organizationName: string | null,
  identityDocumentNumber: string | null,
) =>
  action(KnmActionsEnum.KNM_GET_CONTROL_OBJECTS_BY_NON_ERVK_ORGANIZATION, {
    organizationSubjectCode,
    inn,
    ogrn,
    ogrnip,
    kpp,
    snils,
    rafpNza,
    organizationName,
    identityDocumentNumber,
  });
export const knmSetErknmReasonFormValue = (reasons: IKnmErknmForm['reasonsErknm'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_ERKNM_REASONS_FORM_VALUE, { reasons });
export const knmSetErpReasonFormValue = (reasons: IKnmErpForm['reasons'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_ERP_REASONS_FORM_VALUE, { reasons });
export const knmSetErpReasonErvkFormValue = (reasons: IKnmErpForm['reasonsErvk'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_ERP_REASONS_ERVK_FORM_VALUE, { reasons });
export const knmSetErpKindErvkVersionIdFormValue = (kindErvk: IKnmErpForm['kindErvk'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_ERP_KIND_ERVK_VERSION_ID_FORM_VALUE, { kindErvk });
export const knmSetPmIsUnableToRefuseFormValue = (isUnableToRefuse: IKnmPmForm['isUnableToRefuse'] | undefined) =>
  action(KnmActionsEnum.KNM_SET_PM_IS_UNABLE_TO_REFUSE_FORM_VALUE, { isUnableToRefuse });

export const knmSetOrderVersionId = (orderVersionId?: string) =>
  action(KnmActionsEnum.KNM_SET_ORDER_VERSION_ID, { orderVersionId });
export const knmSetIsCanBeSigned = (data: boolean) => action(KnmActionsEnum.KNM_SET_IS_CAN_BE_SIGNED, { data });

export const knmGetOrders = (
  knoId: string,
  supervisionId: string,
  conditionId: string,
  isPm: boolean,
  typeId: string,
) => action(KnmActionsEnum.KNM_GET_ORDERS, { knoId, supervisionId, conditionId, isPm, typeId });
export const knmSetOrders = (knmOrders: IKnmOrderSelectOption[], conditionId: string) =>
  action(KnmActionsEnum.KNM_SET_ORDERS, { knmOrders, conditionId });

export const knmClearErvkObjectsOfControl = () => action(KnmActionsEnum.KNM_CLEAR_ERVK_OBJECTS_OF_CONTROL);
export const knmCheckKnoSupervisionLink = () => action(KnmActionsEnum.KNM_CHECK_KNO_SUPERVISION_LINKS);
export const knmSetCheckKnoSupervisionLink = (data: IErvkCorrectionRequiredData) =>
  action(KnmActionsEnum.KNM_SET_CHECK_KNO_SUPERVISION_LINKS, { data });
export const knmCheckChanges = () => action(KnmActionsEnum.KNM_CHECK_CHANGES);
export const knmCheckChangesRequest = () => action(KnmActionsEnum.KNM_CHECK_CHANGES_REQUEST);
export const knmCheckChangesSuccess = (data: boolean) => action(KnmActionsEnum.KNM_CHECK_CHANGES_SUCCESS, { data });
export const knmCheckChangesFailure = () => action(KnmActionsEnum.KNM_CHECK_CHANGES_FAILURE);

export const knmSetChangePlanReasonAfterConfirmEvent = (data: IKnmErknmFormChangePlanReason[]) =>
  action(KnmActionsEnum.KNM_SET_CHANGE_PLAN_REASON_AFTER_CONFIRM, { data });

export const knmGetInvalidFields = (id: number) => action(KnmActionsEnum.KNM_GET_INVALID_FIELDS, { id });
export const knmSetInvalidFields = (fields: string[]) => action(KnmActionsEnum.KNM_SET_INVALID_FIELDS, { fields });
export const knmClearInvalidFields = () => action(KnmActionsEnum.KNM_CLEAR_INVALID_FIELDS);

export const knmErvkProsecutorGet = (uuid: string, versionId: string) =>
  action(KnmActionsEnum.KNM_ERVK_PROSECUTOR_GET, { uuid, versionId });
export const knmErvkProsecutorGetRequest = () => action(KnmActionsEnum.KNM_ERVK_PROSECUTOR_REQUEST);
export const knmErvkProsecutorGetSuccess = (data: IUserOrganisation) =>
  action(KnmActionsEnum.KNM_ERVK_PROSECUTOR_SUCCESS, { data });
export const knmErvkProsecutorGetFailure = () => action(KnmActionsEnum.KNM_ERVK_PROSECUTOR_FAILURE);

export const knmGetKnoCollaboratingOrganisations = (
  searchQuery?: string,
  knoLevelCode?: string,
  organisationsParentId?: string,
) =>
  action(KnmActionsEnum.KNM_GET_KNO_COLLABORATING_ORGANISATIONS, {
    searchQuery,
    knoLevelCode,
    organisationsParentId,
  });
export const knmSetKnoCollaboratingOrganisations = (
  knoOrganisations: IKnoOrganisationChild[],
  organisationsPage: string,
  organisationsPageCount: number,
  organisationsTotalCount: number,
) =>
  action(KnmActionsEnum.KNM_SET_KNO_COLLABORATING_ORGANISATIONS, {
    knoOrganisations,
    organisationsPage,
    organisationsPageCount,
    organisationsTotalCount,
  });
export const knmGetKnoCollaboratingOrganisationsNextPage = (searchQuery?: string, knoLevelCode?: string) =>
  action(KnmActionsEnum.KNM_GET_KNO_COLLABORATING_ORGANISATIONS_NEXT_PAGE, { searchQuery, knoLevelCode });

export const knmSetIsShowApproveRemarks = (isShow: boolean) =>
  action(KnmActionsEnum.KNM_SET_IS_SHOW_APPROVE_REMARKS, { isShow });

export const knmSetLoading = (loading: boolean) => action(KnmActionsEnum.KNM_SET_LOADING, { loading });

export const knmLateChangesFieldsGetRequest = () => action(KnmActionsEnum.KNM_GET_LATE_CHANGES_FIELDS_REQUEST);
export const knmLateChangesFieldsGetSuccess = (data: IKnmHistoryItem[] | null) =>
  action(KnmActionsEnum.KNM_GET_LATE_CHANGES_FIELDS_SUCCESS, { data });
export const knmLateChangesFieldsGetFailure = () => action(KnmActionsEnum.KNM_GET_LATE_CHANGES_FIELDS_FAILURE);
export const setKnmLateChangesFieldLinks = (data: Map<string, string>) =>
  action(KnmActionsEnum.KNM_SET_LATE_CHANGES_FIELDS_LINKS, { data });
export const knmFillOrganizationsAndObjectsByOtherPassport = (
  value: {
    organizations: IKnmSubject248[];
    objects: IKnmObject248[];
    events: IKnmEvent248[] | null;
    requirements: IKnmRequirementsNpa248[] | null;
  },
  link: IKnmLink,
) =>
  action(KnmActionsEnum.KNM_FILL_ORGANIZATIONS_AND_OBJECTS_BY_OTHER_PASSPORT, {
    value,
    link,
  });

export const knmSetIsNeedActSaveOrSign = (value: boolean) =>
  action(KnmActionsEnum.KNM_SET_IS_NEED_ACT_SAVE_OR_SIGN, { value });

export const knmUpdateEvidencesByDecisionLinks = (data: IKnmLink[]) =>
  action(KnmActionsEnum.KNM_UPDATE_EVIDENCES_BY_DECISION_LINKS, { data });

export const knmSendAgreement = () => action(KnmActionsEnum.KNM_SEND_AGREEMENT);
export const knmErknmCancelPrescribe = (data: IErknmPrescribeCancel) =>
  action(KnmActionsEnum.KNM_ERKNM_CANCEL_PRESCRIBE, { data });

export const knmSetExternalParams = (value: Record<string, string>) =>
  action(KnmActionsEnum.KNM_SET_EXTERNAL_PARAMS, { value });

export const knmSetIsSaveOnProcess = (value: boolean) => action(KnmActionsEnum.KNM_SET_IS_SAVE_ON_PROCESS, { value });
